import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  Checkbox,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Sync as SyncIcon,
  SyncProblem as SyncProblemIcon,
  WifiOff as WifiOffIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import dailyLogService from '../../services/dailyLogService';

const DailyLog = () => {
  const navigate = useNavigate();
  const [dailyLog, setDailyLog] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [syncStatus, setSyncStatus] = useState({ pendingCount: 0, syncedCount: 0, localOnlyCount: 0, isOnline: true });
  const [syncing, setSyncing] = useState(false);
  // const [lastSyncTime, setLastSyncTime] = useState(null); // Removed last sync time
  const [hasChanges, setHasChanges] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Initialize hourly data structure (12:00 AM to 11:00 PM - one full day)
  const initializeHourlyData = () => {
    const data = {};
    // 0-23 hours (12:00 AM to 11:00 PM)
    for (let hour = 0; hour <= 23; hour++) {
      data[`hour_${hour}`] = {
        hour: hour,
        location: '',
        safetyAudit: false,
        notes: ''
      };
    }
    return data;
  };

  // Load initial data
  useEffect(() => {
    // Get current user ID from localStorage
    const userId = localStorage.getItem('userId');
    console.log('DailyLog component - setting userId:', userId);
    setCurrentUserId(userId);
    
    // Load data after userId is set
    if (userId) {
      loadData();
      checkSyncStatus();
    }
    
    // Removed auto-check sync status and auto-save
    
    return () => {
      // Cleanup
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load locations
      const locationsData = await dailyLogService.getLocations();
      setLocations(locationsData);
      
      // Load or create today's log for current user (from IndexedDB ONLY)
      let logData = await dailyLogService.getCurrentUserLog();
      console.log('Loaded log from IndexedDB:', logData);
      
      if (!logData) {
        // Create new log with initialized hourly data
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const today = `${year}-${month}-${day}`;
        
        // Get userId directly from localStorage
        const userId = localStorage.getItem('userId');
        console.log('Creating new log for userId:', userId);
        
        logData = {
          id: null,
          personId: userId,
          date: today,
          hourlyData: initializeHourlyData(),
          syncStatus: 'pending',
          localId: `local_${Date.now()}`
        };
        
        // ВАЖНО: Сохраняем новый лог в IndexedDB сразу!
        console.log('Creating new daily log in IndexedDB:', logData);
        const savedLog = await dailyLogService.saveCurrentUserLog(logData);
        logData = savedLog;
      } else if (!logData.hourlyData || Object.keys(logData.hourlyData).length === 0) {
        // If log exists but has no hourly data, initialize it
        console.log('WARNING: Log exists but has no hourlyData, initializing empty data');
        logData.hourlyData = initializeHourlyData();
        // НЕ СОХРАНЯЕМ СРАЗУ - это перезапишет существующие данные!
        // const savedLog = await dailyLogService.saveCurrentUserLog(logData);
        // logData = savedLog;
      }
      
      console.log('Final logData to display:', {
        localId: logData.localId,
        id: logData.id,
        hourlyDataKeys: logData.hourlyData ? Object.keys(logData.hourlyData).length : 0,
        sampleData: logData.hourlyData ? logData.hourlyData.hour_9 : null
      });
      
      setDailyLog(logData);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkSyncStatus = async () => {
    try {
      const status = await dailyLogService.getSyncStatus();
      setSyncStatus(status);
      
      // Removed last sync time tracking
    } catch (err) {
      console.error('Failed to check sync status:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      setSyncing(true);
      
      // Save or update the log
      const savedLog = await dailyLogService.saveCurrentUserLog(dailyLog);
      setDailyLog(savedLog);
      
      // Try to sync if online
      if (navigator.onLine) {
        const syncResult = await dailyLogService.syncPendingLogs();
        if (syncResult.synced > 0) {
          setSuccess(`Daily log saved and synced successfully`);
        } else {
          setSuccess(`Daily log saved locally`);
        }
      } else {
        setSuccess(`Daily log saved locally (will sync when online)`);
      }
      
      setHasChanges(false);
      await checkSyncStatus();
    } catch (err) {
      console.error('Error saving log:', err);
      setError('Failed to save daily log');
    } finally {
      setSyncing(false);
    }
  };

  const handleHourChange = async (hourKey, field, value) => {
    const updatedLog = {
      ...dailyLog,
      hourlyData: {
        ...dailyLog.hourlyData,
        [hourKey]: {
          ...dailyLog.hourlyData[hourKey],
          [field]: value
        }
      }
    };
    
    setDailyLog(updatedLog);
    setHasChanges(true);
    
    // Removed auto-save - only save on button click
  };

  const formatHour = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    return `${displayHour}:00 ${period}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!dailyLog) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load daily log</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'fixed',
      top: '60px', // высота хедера
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: 'background.default'
    }}>
      {/* Header - Fixed Height */}
      <Box 
        sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          flex: '0 0 auto'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(-1)} color="primary" size="large">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5">Daily Log</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={syncing || !hasChanges}
            sx={{ 
              ml: 3,
              minWidth: { xs: 100, sm: 150 },
              height: { xs: 32, sm: 36 }
            }}
          >
            {syncing ? 'Saving...' : 'Save Daily Log'}
          </Button>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          {!navigator.onLine && (
            <Chip
              icon={<WifiOffIcon />}
              label="Offline"
              color="warning"
              size="small"
            />
          )}
          {syncStatus.pendingCount > 0 && (
            <Chip
              icon={<SyncProblemIcon />}
              label={`${syncStatus.pendingCount} pending`}
              color="warning"
              size="small"
            />
          )}
        </Box>
      </Box>

      {/* Table Container - Flexible Height */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          flex: '1 1 auto',
          overflow: 'auto'
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: 1, py: 0.5, width: '90px', backgroundColor: 'background.paper', fontSize: '0.8rem' }}>Time</TableCell>
              <TableCell sx={{ px: 1, py: 0.5, width: '30%', maxWidth: '220px', backgroundColor: 'background.paper', fontSize: '0.8rem' }}>Location</TableCell>
              <TableCell sx={{ px: 1, py: 0.5, width: '100px', backgroundColor: 'background.paper', fontSize: '0.8rem' }} align="center">Safety Audit</TableCell>
              <TableCell sx={{ px: 1, py: 0.5, backgroundColor: 'background.paper', fontSize: '0.8rem' }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(dailyLog.hourlyData).map(([hourKey, hourData]) => (
              <TableRow key={hourKey} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell sx={{ px: 1, py: 0.75 }}>
                  <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                    {formatHour(hourData.hour)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ px: 1, py: 0.75 }}>
                  <Autocomplete
                    size="small"
                    value={hourData.location || null}
                    onChange={(event, newValue) => {
                      handleHourChange(hourKey, 'location', newValue || '');
                    }}
                    options={locations}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select location"
                        variant="outlined"
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            padding: '0px',
                          },
                          '& .MuiInputBase-input': {
                            padding: { xs: '2px 4px', sm: '4px 8px' },
                          }
                        }}
                      />
                    )}
                    freeSolo
                  />
                </TableCell>
                <TableCell align="center" sx={{ px: 1, py: 0.75 }}>
                  <Checkbox
                    size="small"
                    checked={hourData.safetyAudit || false}
                    onChange={(e) => handleHourChange(hourKey, 'safetyAudit', e.target.checked)}
                    sx={{ padding: '6px' }}
                  />
                </TableCell>
                <TableCell sx={{ px: 1, py: 0.75 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    maxRows={2}
                    value={hourData.notes || ''}
                    onChange={(e) => handleHourChange(hourKey, 'notes', e.target.value)}
                    placeholder="Add notes..."
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '2px 4px', sm: '4px 8px' }
                      }
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Error/Success Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DailyLog;